import { useEffect, useRef } from "react";
import * as echarts from "echarts";
import { useAppZoom } from "../../contexts/AppZoomContext";

/**
 * CSS `zoom` on #root offsets canvas pointer hit-testing (ECharts and Chart.js).
 * Cancel that zoom on the chart host, then scale back visually
 * (same workaround as the filling-rate line chart on dashboard_v2).
 *
 * The compensation layer is absolutely centered so the extra size
 * cannot stretch the parent or pin the chart to the top-left.
 */
export const getEchartsZoomCompensation = (zoomLevel) => {
  const zoomFactor = (Number(zoomLevel) || 100) / 100;
  const isDisplayZoomed = zoomFactor > 0 && zoomFactor !== 1;
  const compensationStyle = isDisplayZoomed
    ? {
        width: `${100 / zoomFactor}%`,
        height: `${100 / zoomFactor}%`,
        flexShrink: 0,
        zoom: String(1 / zoomFactor),
        transform: `scale(${zoomFactor})`,
        transformOrigin: "center center",
      }
    : {
        width: "100%",
        height: "100%",
        flexShrink: 0,
      };

  return { zoomFactor, isDisplayZoomed, compensationStyle };
};

export const useEchartsZoomCompensation = () => {
  const { zoomLevel } = useAppZoom();
  return { zoomLevel, ...getEchartsZoomCompensation(zoomLevel) };
};

export const EchartsZoomFrame = ({
  width = "100%",
  height = "100%",
  className,
  style,
  chartId,
  chartRef,
}) => {
  const { compensationStyle, zoomLevel } = useEchartsZoomCompensation();
  const wrapperRef = useRef(null);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return undefined;

    const resize = () => {
      const el = chartRef?.current;
      if (!el) return;
      echarts.getInstanceByDom(el)?.resize();
    };

    let observer = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(resize);
    }
    observer?.observe(wrapper);
    window.addEventListener("resize", resize);
    resize();

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", resize);
    };
  }, [chartRef, zoomLevel]);

  return (
    <div
      ref={wrapperRef}
      className={className}
      style={{
        width,
        height,
        ...style,
        position: "relative",
        overflow: "hidden",
        minWidth: 0,
        minHeight: 0,
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={compensationStyle}>
          <div
            id={chartId}
            ref={chartRef}
            style={{ width: "100%", height: "100%" }}
          />
        </div>
      </div>
    </div>
  );
};
